import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ItemSelect from 'commons/components/ItemSelect';
import ToggleSwitch from 'commons/components/ToggleSwitch';
import PCSubscriptionSelector from './PCSubscriptionSelector';
import RegistrationSection from 'UserRegistration/components/RegistrationSteps/RegistrationSection';
import SubscriptionPackage from 'UserRegistration/components/SubscriptionPackage';
import ConfigurationContext from 'UserRegistration/contexts/ConfigurationContext';
import DataContext from 'UserRegistration/contexts/DataContext';
import { enableOrDisableBillingComponents, updateBillingRequired } from 'UserRegistration/contexts/configs/componentConfig';
import { fetchPackageSelectorData } from 'UserRegistration/resources/api/configurationData';
import { isSaveButtonDisabled } from 'UserRegistration/contexts/configs/componentConfig';
import { pushRegistrationSavePackageSelection, pushCheckboxState } from 'UserRegistration/resources/utils/analytics';
import { saveSection } from 'UserRegistration/resources/utils/saveUtils';
import SynchronizedSubscriptionItemSelector from './SynchronizedSubscriptionItemSelector';
import { getPerkList } from 'UserRegistration/components/SubscriptionPackage';
import { synchronizeSelectedSubscriptionItems, getEnabledItemIds, getSelectedItemAssociatedKeys, calculateTotal } from '../../../resources/utils/packageSelectionUtils';

function BasePackageSelector(props) {

    const { componentConfig, setComponentConfig } = useContext(ConfigurationContext);
    const { registrationData, setRegistrationData } = useContext(DataContext);
    /* A ref is created pointing to registrationData because the variable is updated from within useEffect. 
     * Because it's a context state variable, without a ref, there is a risk of a stale version of the variable being accessed 
     * and subsequently used to update an already more recent version of the state. This is particularly dangerous because 
     * many different concurrently running components update the registrationData object.
     */
    const registrationDataRef = useRef(registrationData);
    const [ packageSelectorData, setPackageSelectorData ] = useState({
        availableFrequencies: [],
        availableSubscriptionItemGroups: [],
        preselectedSubscriptionItemId: [],
        purchasingGroupId: null,
        purchasingGroupDescription: null,
        promoCode: null
    });
    const { t } = useTranslation(componentConfig.i18nNamespaces);
    /**
     * The reason we use this variable to determine if the save and continue button is enabled
     * is because the other sections use formik and its refs to detemine the state of the button.
     * There is no formik refs available at this point in the registration.
    */
    const [isWaitingOnResponse, setIsWaitingOnResponse] = useState(false);
    // Calculate the number of packages with the STANDARD or RESTRICTED (if buyer deal is selected via the URL) group type.
    // These are the packages that are displayed in the package selector. Secondary types are not.
    const standardOrRestrictedPackagesCount = packageSelectorData.availableSubscriptionItemGroups.filter((itemGroup) => {
        return itemGroup.groupType === 'STANDARD' || itemGroup.groupType === 'RESTRICTED';
    }).length;
    
    /** Mapping of supported sub components used to render additional subscription items. */
    const subComponentMapping = {
        PCSubscriptionSelector: PCSubscriptionSelector
    }


    useEffect(() => {
        registrationDataRef.current = registrationData;
    });

    // Fetch initial data for subscription packages.
    useEffect(() => {

        fetchPackageSelectorData(props.displayFreePackages,
            props.preselectedSubscriptionItemId,
            props.preselectedGroupId,
            props.defaultFrequency,
            props.promoCode).then((response) => {

            if (response) {

                let registrationDataCopy = Object.assign({}, registrationDataRef.current);

                // Select the frequency to the default.
                registrationDataCopy.registrationModel.subscription.selectedFrequency = response.availableFrequencies.find(frequency => frequency.defaultFrequency)?.code;
                // Set the user selected frequency with the same value.
                registrationDataCopy.metadata.userSelectedFrequency = registrationDataCopy.registrationModel.subscription.selectedFrequency;
                // find the appropriate preselected group id (if any)
                const preselectedGroups = response.availableSubscriptionItemGroups.filter(group => group.preselected);
                let preselectedGroupId = null;
                
                // only a single non-secondary group can be preselected so that we know which package and subscription items to preselect
                const preselectedNonSecondaryGroups = preselectedGroups.filter(group => group.groupType !== "SECONDARY")
                if (preselectedNonSecondaryGroups.length === 1) {
                    preselectedGroupId = preselectedNonSecondaryGroups[0].id;
                }

                // PC group preselection is handled here
                preselectedGroupId = handlePCPreselection(preselectedGroups, preselectedNonSecondaryGroups, response, preselectedGroupId, registrationDataCopy);

                // date formatting pattern
                registrationDataCopy.registrationModel.datePattern = response.datePattern;
                // Promotion Code Details
                if ( response.promoCodeDto) {

                    // This is what we send to the backend on saving/finalizing the registration.
                    registrationDataCopy.registrationModel.subscription.promoCodeId = response.promoCodeDto.promoCodeId;

                    // This data is used for display purposes.
                    registrationDataCopy.registrationModel.promotion.id = response.promoCodeDto.promoCodeId;
                    registrationDataCopy.registrationModel.promotion.code = response.promoCodeDto.promoCode;
                    registrationDataCopy.registrationModel.promotion.description = response.promoCodeDto.promoCodeDescription;
                    registrationDataCopy.registrationModel.promotion.type = response.promoCodeDto.promoCodeType;
                    registrationDataCopy.registrationModel.promotion.amount = response.promoCodeDto.promoCodeAmount;
                    registrationDataCopy.registrationModel.promotion.supportedSubscriptionItems = response.promoCodeDto.supportedSubscriptionItems;
                }
                
                response.availableSubscriptionItemGroups.forEach((group) => {
                    
                    // If we have a preselected groupId, we can set select the right package for the user.
                    // Check if the IDs match our preselectedGroupId.
                    if (preselectedGroupId && group.id == preselectedGroupId) {

                        group.selected = true;
                        const isFreePackage = group.freePackage;

                        // If there's only one subscription item under this group, we can also preselect the subscription item.
                        if (group.subscriptionItems.length === 1) {
                            registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds = [group.subscriptionItems[0].id];
                            registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemDescription = group.subscriptionItems[0].description;
                            registrationDataCopy.priceTaxesInfo.orderedPackage = group.subscriptionItems[0].description;
                            registrationDataCopy.registrationModel.selectedSubscriptionPackage = group;
                        } else if (group.subscriptionItems.length > 1 && props.preselectedSubscriptionItemId) {
                            const preselectedSubscriptionItems = group.subscriptionItems.filter((item) => {
                                return item.preselected;
                            });

                            for (const preselectedItem of preselectedSubscriptionItems) {
                                registrationDataCopy.priceTaxesInfo.orderedPackage = preselectedItem.description;
                                if (!Array.isArray(registrationDataCopy.selectedSubscriptionItems[group.groupKey])) {
                                    registrationDataCopy.selectedSubscriptionItems[group.groupKey] = [];
                                    registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds = [];
                                    registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionAssociatedItemKeys = [];
                                }

                                registrationDataCopy.selectedSubscriptionItems[group.groupKey].push(preselectedItem.id);
                                registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds.push(preselectedItem.id);
                                registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionAssociatedItemKeys.push(preselectedItem.associatedItemKey);
                                registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemDescription = preselectedItem.description;
                                registrationDataCopy.registrationModel.selectedSubscriptionPackage = group;
                                registrationDataCopy.preSelectedSubItemAssociatedKey = preselectedItem.associatedItemKey;
                            }

                            if (props.showSynchronizedSubscriptionItemSelector) {
                                // pass in "response" here because the state variable packageSelectorData hasn't been set yet
                                registrationDataCopy = synchronizeSelectedSubscriptionItems(registrationDataCopy, response);
                            }

                        }

                        registrationDataCopy.invoiceItemIDs = registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds;

                        if (isFreePackage) {
                            registrationDataCopy = setFreePackage(registrationDataCopy, group);
                        }
                        
                        registrationDataCopy.registrationModel.subscription.selectedGroup.description = group.description;
                        registrationDataCopy.registrationModel.subscription.selectedGroup.groupKey = group.groupKey;
                        registrationDataCopy.registrationModel.subscription.selectedGroup.cmsKey = group.cmsKey;

                    } else if (props.purchasingGroupId) {

                        const pgItemIds = group.subscriptionItems.filter((item) => item.purchasingGroupId == props.purchasingGroupId).map((item) => {return item.id});
                        registrationDataCopy.selectedSubscriptionItems[group.groupKey] = pgItemIds;
                        group.selectedSubscriptionAssociatedItemKeys = getSelectedItemAssociatedKeys(pgItemIds, response);
                        
                    }
                });

                registrationDataCopy.registrationModel.subscription.selectedGroup.groupId = preselectedGroupId;
                
                // When the synchronized subscription item selector is activated, then the user chooses a subscription item from this widget instead of on each package.
                if (props.showSynchronizedSubscriptionItemSelector && registrationDataCopy.preSelectedSubItemAssociatedKey) {
                    
                    // Disable any packages that do not have a corresponding subscription item to the one that was preselected.
                    response.availableSubscriptionItemGroups.forEach((group) => {
                        // synch across groups
                        const matches = group.subscriptionItems.filter((item) => registrationDataCopy.preSelectedSubItemAssociatedKey == item.associatedItemKey);

                        group.enabled = matches.length === 0 ? false : true;

                    });
                }

                // Now that the registrationDataCopy has been properly set, update the perk list for each group
                response.availableSubscriptionItemGroups.forEach((group) => {
                    group.perkList = getPerkList(group, registrationDataCopy, t);
                });

                setPackageSelectorData(response);
                setRegistrationData(registrationDataCopy);

            }

        });
    }, []);

    const setFreePackage = (registrationDataCopy) => {
        // only remove the billing/payment section when passed the first step and no vas selected
        if (registrationDataCopy.registrationModel.subscription.vasItemIds.length === 0 && 
            registrationData.registrationModel.selectedSecondarySubscriptions.length === 0 &&
            registrationDataCopy.metadata.currentStep > 1) {
            enableOrDisableBillingComponents(componentConfig, setComponentConfig, false);
            registrationDataCopy.metadata.isBillingRequired = false;
        }

        return registrationDataCopy;
    }

    const saveAndContinue = () => {
        updateBillingRequired(registrationData, componentConfig, setComponentConfig);
        updateTotal();
        setIsWaitingOnResponse(true);
        saveSection(componentConfig, registrationData, setRegistrationData, pushRegistrationSavePackageSelection);
    };

    /**
     * METS-853 : EAP not being added to Order Details
     */
    const updateTotal = () => {
        calculateTotal(registrationDataRef.current, setRegistrationData);
    }

    /**
     * The main orchestrator function that will handle all the business rules for preselecting PC subscription items
     * Follows the General Rules outlined in METS-2928.
     */
    const handlePCPreselection = (preselectedGroups, preselectedNonSecondaryGroups, response, preselectedGroupId, registrationDataCopy) => {
        const preselectedPcGroup = preselectedGroups.find(i => i.groupKey === "PRIVATE_CONSTRUCTION")
        if (preselectedPcGroup) {
            // Only preselect the free package if none of the non-secondary paying packages are selected
            const isStandardPayingPackagePreselected = preselectedNonSecondaryGroups.some(i => i.freePackage === false);
            let freeGroup = response.availableSubscriptionItemGroups.find(i => i.freePackage === true);
            if (freeGroup && !isStandardPayingPackagePreselected) {
                freeGroup.selected = true;
                freeGroup.preselected = true;
                preselectedGroupId = freeGroup.id;
            }

            // if root level is preselected along with any preselected children, then don't do any preselection since
            // this violates the rules defined in METS-2928
            const rootPCItem = preselectedPcGroup.subscriptionItems[0];
            if (!isPreselectedAndHasPreselectedChildren(rootPCItem, response.preselectedSubscriptionItemId)) {
                preselectPCSubscriptionItems(rootPCItem, response.preselectedSubscriptionItemId, registrationDataCopy)
            }
        }
        return preselectedGroupId;
    }

    /**
     * Adds any PC subscription item to the selectedSecondarySubscriptions list if they are considered preselected.
     * Follows the General Rules outlined in METS-2928. If any rule is violated, then we won't preselect any PC
     * subscription item.
     */
    const preselectPCSubscriptionItems = (pcSubscriptionItem, preselectedIds, registrationDataCopy) => {
        if (!preselectedIds) {
            return;
        }

        // if this PC sub item has reached its max selectable children, then the specified preselection is invalid
        if (pcSubscriptionItem.children.length > 0) {
            const selectedChildren = pcSubscriptionItem.children.filter(child => preselectedIds.includes(child.id));
            if (selectedChildren.length > pcSubscriptionItem.maxSelectableChildren) {
                return;
            }
        }

        // if it's preselected, then add it to selectedSecondarySubscriptions, and make sure preselected is true
        if (preselectedIds.includes(pcSubscriptionItem.id)) {
            pcSubscriptionItem.preselected = true;
            registrationDataCopy.registrationModel.selectedSecondarySubscriptions.push(pcSubscriptionItem);
        }

        // recursively handle the preselection of children
        pcSubscriptionItem.children.forEach((childPcSubItem) => {
            preselectPCSubscriptionItems(childPcSubItem, preselectedIds, registrationDataCopy);
        });
    }

    // Helper function to check if a PC item is preselected and has any preselected (grand)children 
    const isPreselectedAndHasPreselectedChildren = (pcSubscriptionItem, preselectedIds) => {
        if (!preselectedIds) {
            return false;
        }

        // check if the current level has "selected" set to true
        const currentLevelSelected = preselectedIds.includes(pcSubscriptionItem.id);

        let pcFlatList = buildFlattenedPCSubItems(pcSubscriptionItem);
        const anyChildSelected = pcFlatList.some(i => i.id !== pcSubscriptionItem.id && preselectedIds.includes(i.id))

        // return true only if both the current level and at least one child are selected
        return currentLevelSelected && anyChildSelected;
    }

    // Helper function to build a flattened list of all the PC subscription items
    const buildFlattenedPCSubItems = (pcSubItem, pcFlatList = []) => {
        pcFlatList.push(pcSubItem);

        if (pcSubItem.children && pcSubItem.children.length > 0) {
            for (const child of pcSubItem.children) {
                buildFlattenedPCSubItems(child, pcFlatList);
            }
        }

        return pcFlatList;
    }

    // Display name of the currently selected frequency: Monthly, Yearly, etc.
    const currentFrequencyDisplayName = packageSelectorData.availableFrequencies.length ? packageSelectorData.availableFrequencies.find((value) => {
        return value.code === registrationData.registrationModel.subscription.selectedFrequency;
    })?.displayName : null;

    /**
     * Renders the frequency selector. If there is only 1 frequency available then no widget will be displayed.
     */
    const renderFrequencySelector = () => {

        if (packageSelectorData.availableFrequencies.length) {

            // No need for a frequency selector if there is only 1!
            if (packageSelectorData.availableFrequencies.length === 1) {
                return null;
            } else if (packageSelectorData.availableFrequencies.length === 2) {

                // First Frequency will be the first one in the list and will be the "OFF" choice.
                const firstFrequency = packageSelectorData.availableFrequencies[0];
                // Second Frequency will be the second one in the list and will be the "ON" choice.
                const secondFrequency = packageSelectorData.availableFrequencies[1];
                // We will need to update the context registrationData, so copy the object for local modification which will then be updated using state.
                const registrationDataCopy = Object.assign({}, registrationData);
                const label = t("frequency."+ secondFrequency.code +".toggle", "") + " " + t("frequency."+ secondFrequency.code +".subName", "");
                const offLabel = t("frequency."+ firstFrequency.code +".toggle", "") + " " + t("frequency."+ firstFrequency.code +".subName", "");

                // If there's only two, we can render a lovely ToggleSwitch.
                return <ToggleSwitch 
                            alwaysOn={true} 
                            checked={registrationDataCopy.metadata.userSelectedFrequency === secondFrequency.code}
                            disabled={!registrationDataCopy.metadata.isBillingRequired}
                            idPrefix="packageSelectorFrequency"
                            label={label}
                            offLabel={offLabel}
                            onChange={(e) => {

                                // Update the Selected Frequency.
                                registrationDataCopy.registrationModel.subscription.selectedFrequency = e.target.checked ? secondFrequency.code : firstFrequency.code;
                                registrationDataCopy.metadata.userSelectedFrequency = registrationDataCopy.registrationModel.subscription.selectedFrequency;

                                // Find the selected package, and deselect it if it doesn't support the selected frequency
                                packageSelectorData.availableSubscriptionItemGroups.forEach((subscriptionPackage) => {
                                    if (subscriptionPackage.selected) {
                                        const isAvailableForSelectedFrequency = subscriptionPackage.displayPrices[registrationDataCopy.registrationModel.subscription.selectedFrequency] !== undefined;

                                        if (!isAvailableForSelectedFrequency && !subscriptionPackage.freePackage) {
                                            deselectCurrentPackage(subscriptionPackage, registrationDataCopy);
                                        }
                                    }
                                });


                                pushCheckboxState("PackageFrequencySelector", e.target.checked ? label : offLabel);

                                setRegistrationData(registrationDataCopy);

                            }}
                            name="packageSelectorFrequency"
                            title={currentFrequencyDisplayName}
                            readOnly={true}
                        />

            } else {
                // For 3 or more, render an ItemSelect.
                return <ItemSelect
                    idPrefix="packageSelectorFrequency"
                    disabled={!registrationData.metadata.isBillingRequired}
                    selectedItem={registrationData.registrationModel.subscription.selectedFrequency}
                    items={packageSelectorData.availableFrequencies.map((item) => {
                        return { label: t("frequency."+ item.code +".toggle", ""), value: item.code };
                    })}
                    onItemSelect={(selectedItem) => {

                        // We will need to update the context registrationData, so copy the object for local modification which will then be updated using state.
                        const registrationDataCopy = Object.assign({}, registrationData);

                        registrationDataCopy.registrationModel.subscription.selectedFrequency = selectedItem.value;
                        registrationDataCopy.metadata.userSelectedFrequency = registrationDataCopy.registrationModel.subscription.selectedFrequency;

                        // Find the selected package.
                        packageSelectorData.availableSubscriptionItemGroups.forEach((subscriptionPackage) => {

                            if (subscriptionPackage.selected) {

                                const isAvailableForSelectedFrequency = subscriptionPackage.displayPrices[registrationDataCopy.registrationModel.subscription.selectedFrequency] !== undefined;

                                if (!isAvailableForSelectedFrequency) {
                                                                        deselectCurrentPackage(subscriptionPackage);
                                }

                                // Then update the order total with the new frequency.
                                registrationDataCopy.priceTaxesInfo.subTotal = subscriptionPackage.totalPrices[registrationDataCopy.registrationModel.subscription.selectedFrequency] || 0;

                                // Exit Loop.
                                return;
                            }

                        });

                        setRegistrationData(registrationDataCopy);
                    }}
                    placeholder={t("chooseBillingFrequency")}
                />
            }

        }

        return null;

    };

    const deselectCurrentPackage = (currentPackage, registrationDataCopy = null) => {

        currentPackage.selected = false;
        
        // if no copy of the registrationData is provided, then we will create our own copy. 
        if (!registrationDataCopy) {
            registrationDataCopy = Object.assign({}, registrationData);
        }
        
        // Update the selected group package.
        registrationDataCopy.registrationModel.subscription.selectedGroup.groupId = null;
        registrationDataCopy.registrationModel.subscription.selectedGroup.cmsKey = null;
        registrationDataCopy.registrationModel.subscription.selectedGroup.groupKey = null;
        registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds = [];
        registrationDataCopy.registrationModel.selectedSubscriptionPackage = null;

        // Update the order total.
        registrationDataCopy.priceTaxesInfo.subTotal = 0;
        registrationDataCopy.priceTaxesInfo.orderedPackage = null;

        registrationDataCopy.invoiceItemIDs = registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds;

        setRegistrationData(registrationDataCopy);

    };

    const renderSubscriptionPackageContent = () => {

        if (packageSelectorData.availableSubscriptionItemGroups.length) {

            return (
                <>
                    <div className={`subscription-package-descriptions package-count-${standardOrRestrictedPackagesCount}`}>
                        {packageSelectorData.availableSubscriptionItemGroups.map((subscriptionPackage, index) => {
    
                            if (subscriptionPackage.groupType === 'STANDARD') {

                                const subscriptionPackageDescription = t('supplier.services.group.description.' + subscriptionPackage.groupKey, '');
    
                                return (
                                    <div className="description-column" key={index}>
                                        {subscriptionPackageDescription ? subscriptionPackageDescription : ''}
                                    </div>
                                );

                            }

                        })}
                    </div>
    
                    <div className={`subscription-packages package-count-${standardOrRestrictedPackagesCount}`}>
                        {packageSelectorData.availableSubscriptionItemGroups.map((subscriptionPackage, index, array) => {
                            return renderSubscriptionPackage(subscriptionPackage, index, array);
                        })}
                    </div>
                </>
            );

        } else {
            return <div className="lds-dual-ring"></div>;
        }

    };

    const renderSubscriptionPackage = (subscriptionPackage, index, array) => {

        const isSelected = subscriptionPackage.selected;
        const isStandardPackage = subscriptionPackage.groupType === 'STANDARD';
        const isRestrictedPackage = subscriptionPackage.groupType === 'RESTRICTED';
        const selectedFrequency = registrationData.metadata.userSelectedFrequency;
        const isAvailableForSelectedFrequency = subscriptionPackage.displayPrices[selectedFrequency] !== undefined;
        const isFreePackage = subscriptionPackage.freePackage;
        // METS-16899: groups that no longer allow Monthly billing (e.g. Regional/National) must not be shown when the
        // Monthly frequency is selected. The backend also omits the Monthly price for these groups, but we guard on the
        // explicit flag as well so the behaviour is clear and independent of the price map.
        const isMonthlyBillingDisabled = selectedFrequency === 'MONTHLY' && subscriptionPackage.allowMonthlyBilling === false;
        /*
         * If the showSynchronizedSubscriptionItemSelector option is enabled, this means the user will select a subscription item (in the form of a state/prov or item that is common to all packages) 
         * from a separate list in another widget. Therefore, we don't show a drop down in each of the selectable packages.
         */
        const displaySubscriptionItemSelect = !props.showSynchronizedSubscriptionItemSelector && subscriptionPackage.displaySubscriptionItemSelect;

        // Verify that the current package should be rendered.
        // If it is not a standard package, or has no price for the selected frequency and isn't free, then don't render it.
        if ((!isStandardPackage && !isRestrictedPackage) || (!isAvailableForSelectedFrequency && !isFreePackage) || isMonthlyBillingDisabled) {
            return null;
        }

        return <SubscriptionPackage
            disabled={!subscriptionPackage.enabled}
            frequency={selectedFrequency}
            isSelected={isSelected}
            displaySubscriptionItemSelect={displaySubscriptionItemSelect}
            key={index}
            //TODO: Put in a const, else this gets regenerated every time there is a click.
            onClickHandler={() => {

                if (subscriptionPackage.enabled) {

                    array.forEach((value) => {
                        if (value.id === subscriptionPackage.id) {
                            value.selected = true;
                        } else {
                            value.selected = false;
                        }
                    });

                    let registrationDataCopy = Object.assign({}, registrationData);

                    // If the package is free, then billing is no longer required and we should force the frequency to 'STANDARD'.
                    if (isFreePackage) {
                        registrationDataCopy = setFreePackage(registrationDataCopy, subscriptionPackage);
                    } else {
                        enableOrDisableBillingComponents(componentConfig, setComponentConfig, true);
                        registrationDataCopy.metadata.isBillingRequired = true;
                        registrationDataCopy.registrationModel.subscription.selectedFrequency = registrationData.metadata.userSelectedFrequency;
                    }

                    // Update the selected group package.
                    registrationDataCopy.registrationModel.subscription.selectedGroup.groupId = subscriptionPackage.id;
                    registrationDataCopy.registrationModel.subscription.selectedGroup.groupKey = subscriptionPackage.groupKey;
                    registrationDataCopy.registrationModel.subscription.selectedGroup.description = subscriptionPackage.description;
                    registrationDataCopy.registrationModel.subscription.selectedGroup.cmsKey = subscriptionPackage.cmsKey;
                    registrationDataCopy.registrationModel.selectedSubscriptionPackage = subscriptionPackage;

                    if (props.showSynchronizedSubscriptionItemSelector) {

                        if (registrationDataCopy.selectedSubscriptionItems[subscriptionPackage.groupKey]) {
                            registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds = registrationDataCopy.selectedSubscriptionItems[subscriptionPackage.groupKey];
                            registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionAssociatedItemKeys = getSelectedItemAssociatedKeys(registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds, packageSelectorData);
                            registrationDataCopy.invoiceItemIDs = getEnabledItemIds(registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds, packageSelectorData);
                        } else {
                            registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds = [];
                            registrationDataCopy.invoiceItemIDs = [];
                        }

                    } else {
                        
                        // When the user clicks on a package, if there is only 1 subscription option for this group, we can automatically select it.
                        if (subscriptionPackage.subscriptionItems.length === 1) {
                            registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds = [subscriptionPackage.subscriptionItems[0].id];
                            registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemDescription = subscriptionPackage.subscriptionItems[0].description;
                            registrationDataCopy.priceTaxesInfo.orderedPackage = subscriptionPackage.subscriptionItems[0].description;
                        } else if (registrationDataCopy.selectedSubscriptionItems[subscriptionPackage.groupKey]) {
                            /*
                            * Otherwise, check if the user has previously selected an item from the dropdown for this package, if so, immediately
                            * update the model with this subscription ID as the selected.
                            */
                            registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds = registrationDataCopy.selectedSubscriptionItems[subscriptionPackage.groupKey];
                            registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemDescription = subscriptionPackage.description;
                            const selectedIndex = subscriptionPackage.subscriptionItems.map(item => String(item.id)).indexOf(registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds[0]);
                            registrationDataCopy.priceTaxesInfo.orderedPackage = subscriptionPackage.subscriptionItems[selectedIndex].description;
                        } else {
                            // Else, simply clear the selectedSubscriptionOptionId as it means the user still needs to choose something from the dropdown.
                            registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds = [];
                            registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemDescription = null;
                            registrationDataCopy.priceTaxesInfo.orderedPackage = null;
                        }

                        // update item ids for Order Details sidebar
                        registrationDataCopy.invoiceItemIDs = registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds;

                    }

                    if (registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemDescription) {
                        packageSelectorData.availableSubscriptionItemGroups.forEach((group) => {
                            group.perkList = getPerkList(group, registrationDataCopy, t);
                        });
                    }

                    setRegistrationData(registrationDataCopy);
                    setPackageSelectorData(packageSelectorData);
                }

            }}
            subscriptionPackage={subscriptionPackage}
        />
    };

    /**
     * Renders sub components defined in the component properties.
     * These will be extra components with their own configuration that 
     * allow the user to choose additional subscription packages or options.
     */
    const renderSubComponents = () => {

        if (packageSelectorData.availableSubscriptionItemGroups.length) {
            return props.subComponents.map((subComponent) => {
                // For each active component, create an instance based on the associated component definition.
                return React.createElement(
                    subComponentMapping[subComponent.componentName],
                    {
                        ...subComponent.parameters,
                        disabled: registrationData.registrationModel.subscription.selectedGroup.groupId == null || registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds.length === 0,
                        packageSelectorData: packageSelectorData
                    }
                );
            });
        }

    }

    return (

        <RegistrationSection
            className="package-selector"
            id="packageSelector"
            saveButtonDisabled={isSaveButtonDisabled(registrationData, componentConfig, packageSelectorData) || isWaitingOnResponse}
            saveButtonOnClick={saveAndContinue}
            sectionNumber={props.sectionNumber}
            title={t('packageSelection')}>

            {renderFrequencySelector()}

            {renderSubscriptionPackageContent()}

            {props.showSynchronizedSubscriptionItemSelector &&
                <SynchronizedSubscriptionItemSelector
                    packageSelectorData={packageSelectorData}
                    setPackageSelectorData={setPackageSelectorData}
                />
            }

            {renderSubComponents()}

        </RegistrationSection>

    );

}

BasePackageSelector.propTypes = {
    /** The frequency that the pckage selector should default to when it first loads. */
    defaultFrequency: PropTypes.string,
    /** Flag that indicates whether or not packages without a cost should be displayed. */
    displayFreePackages: PropTypes.bool,
    /** A key that corresponds to a property of the registrationModel object. @see UserRegistration.js */
    modelKey: PropTypes.string,
    /** The ID of the package group to preselect. */
    preselectedGroupId: PropTypes.number,
    /** List of preselected subscription item IDs to preselect (nullable)*/
    preselectedSubscriptionItemId: PropTypes.array,
    /** The ID of the purchasing group */
    purchasingGroupId: PropTypes.number,
    /** The total number of configurable sections currently loaded in the instance of UserRegistration. */
    sectionCount: PropTypes.number,
    /** The position of this section in relation to other sections in the entire instance of UserRegistration. */
    sectionNumber: PropTypes.number,
    /** Secondary Subscription Items that are not main package types, but allow the user to choose additional subscription options. Components are specified to display these additional items. */
    secondaryGroupSubscriptionComponents: PropTypes.array,
    /** Flag that indicates whether or not to display the synchronized subscription item selector widget. */
    showSynchronizedSubscriptionItemSelector: PropTypes.bool,
    /** An array of sub components to render. */
    subComponents: PropTypes.array,
    /** The promotional code. */
    promoCode: PropTypes.string
};

BasePackageSelector.defaultProps = {
    displayFreePackages: false,
    preselectedSubscriptionItemId: [],
    subComponents: []
};

export default BasePackageSelector;
